import { GetEntityAttributesQuery } from "gql/graphql";
import { createContext, ReactNode, useContext } from "react";
import { useGroupsData } from "../../entity-attributes-group/context/entity-attributes-group-context";
import { useDeleteEntityAttribute } from "@/modules/dynamic-attributes/feature/remove-dynamic-attribute";

type Attribute = Extract<
  GetEntityAttributesQuery["getDynamicAttributeGroupMetaInfo"]["attributes"][number],
  {
    __typename: "SimpleTypeDynamicAttributeDto";
  }
>;

type ContextProps = {
  attributes: Attribute[];
  getAttributeById: (id: string | undefined) => Attribute | undefined;
  remove: (id: string) => void;
  data?: GetEntityAttributesQuery;
};

const Context = createContext<ContextProps>({} as ContextProps);

export function EntityAttributeContext({ children }: { children: ReactNode }) {
  const remove = useDeleteEntityAttribute();
  const { data } = useGroupsData();
  const attributes =
    data?.getDynamicAttributeGroupMetaInfo.attributes.filter(
      (item): item is Attribute =>
        item.__typename === "SimpleTypeDynamicAttributeDto",
    ) ?? [];

  const getAttributeById = (id: string | undefined) => {
    const attribute = attributes.find(
      ({ id: attributeId }) => attributeId === id,
    );

    if (attribute) {
      return attribute;
    }

    return (data?.getDynamicAttributeGroupMetaInfo.groups ?? [])
      .reduce((result, current) => {
        return [
          ...result,
          ...current.attributes.filter(
            (item): item is Attribute =>
              item.__typename === "SimpleTypeDynamicAttributeDto",
          ),
        ];
      }, [] as Attribute[])
      .find(({ id: attributeId }) => id === attributeId);
  };

  return (
    <Context.Provider
      value={{
        attributes,
        getAttributeById,
        remove,
        data,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useEntityAttribute() {
  return useContext(Context);
}
